import { type FormEvent, type ReactElement, useCallback, useEffect, useState } from 'react';
import { type AdministrativeRegionView, type MarketActivationRequestView, type OperatingMarketView, type MarketLaunchStatus, type MarketStatusChangeView, type MarketStatusReasonCode } from '@vello/api-client';
import {
  type MarketForm,
  type MarketFormField,
  type MarketFormIssue,
  emptyMarketForm,
  marketRegionChoices,
  normalizeMarketForm,
  registerMarket,
  listMarkets,
  setMarketStatus,
  marketStatusHistory,
  formatMarketInstant,
  tailOf,
} from '../../src/index';
import { usePersonNames } from '../components/person-name';
import { MARKET_STATUS_REASONS, maySelfApprove } from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { Badge, Button, Field, Notice, PageHeader, Panel, Select, Table, TextInput, type Column, FormRow, SectionTitle, FormGrid, Cluster, Stack, Text, TimelineItem } from '@vello/web-theme';
import { LoadingBlock } from '../components/loading-block';
import { useVocab } from '../i18n/vocab';
import { usePlaceNames } from '../i18n/scope-name';
import { RegionsConsole } from '../components/regions-console';

/** The seven-state operating-market lifecycle (mirrors `@vello/config` `MARKET_LAUNCH_STATES`). */
const STATUSES: readonly MarketLaunchStatus[] = ['DRAFT', 'CONFIGURING', 'PILOT', 'ACTIVE', 'PAUSED', 'EXITING', 'RETIRED'];

const STATUS_TONE: Readonly<Record<MarketLaunchStatus, 'neutral' | 'success' | 'warning' | 'danger'>> = {
  DRAFT: 'warning',
  CONFIGURING: 'warning',
  PILOT: 'neutral',
  ACTIVE: 'success',
  PAUSED: 'neutral',
  EXITING: 'danger',
  RETIRED: 'neutral',
};

/** issue reason code → localized message id (one issue per field). */
const ISSUE_KEY: Readonly<Record<MarketFormIssue['code'], string>> = {
  INVALID_COUNTRY: 'mkt.err.country',
  INVALID_SLUG: 'mkt.err.slug',
  INVALID_NAME: 'mkt.err.name',
  INVALID_TIMEZONE: 'mkt.err.timezone',
  INVALID_REGION: 'mkt.err.region',
  REGION_COUNTRY_MISMATCH: 'mkt.err.regionMismatch',
  INVALID_COORD: 'mkt.err.coord',
  COORD_PAIR: 'mkt.err.coordPair',
};

/**
 * Mercados / Markets (Layer 2; m0101). The operator registers the cities the platform runs as **DB data** — a
 * typed `cc:slug` market with a country, optional region, name, IANA timezone, and optional WGS84 center — then
 * advances its lifecycle status from the list. A city is registered data, not a hardcoded literal, and the
 * operator scope selector reads this catalog. Country/name/timezone are data — the form hardcodes no country.
 */
export function MarketsScreen(): ReactElement {
  const { runtime, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator approves their own activation request
  const place = usePlaceNames();
  const { t, lang } = useT();
  const { v, fail, explain } = useVocab();
  const { confirm } = useConsole();

  // ---- register-a-market form ----
  const [form, setForm] = useState<MarketForm>(() => emptyMarketForm());
  const [errors, setErrors] = useState<Partial<Record<MarketFormField, string>>>({});
  const [createMsg, setCreateMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  const [createBusy, setCreateBusy] = useState(false);
  // ADR-0124: the states of the typed country - the market's state is chosen from them, never typed.
  const [regions, setRegions] = useState<readonly AdministrativeRegionView[]>([]);
  const regionCountry = form.countryCode.trim().toUpperCase();
  useEffect(() => {
    if (!/^[A-Z]{2}$/.test(regionCountry)) {
      setRegions([]);
      return undefined;
    }
    let cancelled = false;
    void runtime.markets.regions(regionCountry).then((res) => {
      if (!cancelled) setRegions(res.ok ? res.value.regions : []);
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, regionCountry]);
  const regionChoices = marketRegionChoices(regions, regionCountry);
  // A state of another country than the one typed is dropped with the country change.
  useEffect(() => {
    setForm((f) => (f.regionCode !== '' && !f.regionCode.toUpperCase().startsWith(`${regionCountry}-`) ? { ...f, regionCode: '' } : f));
  }, [regionCountry]);

  // ---- catalog (lookup + list) ----
  const [lookup, setLookup] = useState('');
  const [markets, setMarkets] = useState<readonly OperatingMarketView[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [listMsg, setListMsg] = useState<string | undefined>(undefined);
  const [listBusy, setListBusy] = useState(false);

  // ---- status change ----
  const [statusBusy, setStatusBusy] = useState<string | undefined>(undefined);
  const [statusMsg, setStatusMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);

  // ---- D420: the recorded status history of one market ----
  const [history, setHistory] = useState<{ market: OperatingMarketView; changes: readonly MarketStatusChangeView[] | undefined; error?: string } | undefined>(undefined);
  const actorName = usePersonNames((history?.changes ?? []).map((c) => c.actorId));

  // ---- D449 (the D483 carry): four eyes on ACTIVE — the proposals waiting for another operator ----
  const [activations, setActivations] = useState<readonly MarketActivationRequestView[]>([]);
  const loadActivations = useCallback(async (): Promise<void> => {
    const res = await runtime.markets.listActivations();
    setActivations(res.ok ? res.value.requests : []);
  }, [runtime]);
  useEffect(() => {
    void loadActivations();
  }, [loadActivations]);
  const decideActivation = async (r: MarketActivationRequestView, action: 'approve' | 'reject' | 'withdraw'): Promise<void> => {
    let note: string | undefined;
    if (action === 'reject') {
      const answer = await confirm({ title: t('opr.queue.reject'), body: t('opr.queue.rejectNote'), confirmLabel: t('opr.queue.reject'), cancelLabel: t('ui.cancel'), tone: 'danger', notePlaceholder: t('opr.queue.rejectNote') });
      if (!answer.confirmed || answer.note === undefined || answer.note.trim() === '') return;
      note = answer.note;
    }
    const res = await runtime.markets.decideActivation(r.id, action, note);
    if (res.ok) {
      setStatusMsg({ tone: 'success', text: action === 'approve' ? t('mact.approved', { id: r.marketId }) : t('opr.done') });
      const next = res.value.market;
      if (next !== undefined) setMarkets((ms) => ms.map((m) => (m.id === next.id ? next : m)));
    } else {
      setStatusMsg({ tone: 'danger', text: t('mkt.status.failed', { code: fail(res.error) }) });
    }
    await loadActivations();
  };

  const set = (field: keyof MarketForm) => (v: string): void => setForm((f) => ({ ...f, [field]: v }));

  const refresh = async (country?: string): Promise<void> => {
    setListBusy(true);
    setListMsg(undefined);
    const res = await listMarkets(runtime, country);
    setListBusy(false);
    setLoaded(true);
    if (res.ok) setMarkets(res.value.markets);
    else setListMsg(t('mkt.lookup.failed', { code: res.error.code }));
  };

  const onCreate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setCreateMsg(undefined);
    const normalized = normalizeMarketForm(form);
    if (!normalized.ok) {
      const next: Partial<Record<MarketFormField, string>> = {};
      for (const issue of normalized.error) next[issue.field] = t(ISSUE_KEY[issue.code]);
      setErrors(next);
      return;
    }
    setErrors({});
    setCreateBusy(true);
    const res = await registerMarket(runtime, normalized.value);
    setCreateBusy(false);
    if (res.ok) {
      setCreateMsg({ tone: 'success', text: t('mkt.created', { id: res.value.market.id }) });
      setForm(emptyMarketForm());
      await refresh();
    } else {
      // MARKET_UNKNOWN_REGION (the state is not a registered state of the country) and any other stable reason read through the vocabulary.
      const text = res.error.code === 'CONFLICT' ? t('mkt.create.conflict') : (explain(res.error) ?? t('mkt.create.failed', { code: res.error.code }));
      setCreateMsg({ tone: 'danger', text });
    }
  };

  const onLookup = async (): Promise<void> => {
    const cc = lookup.trim().toUpperCase();
    if (cc !== '' && !/^[A-Z]{2}$/.test(cc)) {
      setListMsg(t('mkt.err.country'));
      return;
    }
    await refresh(cc === '' ? undefined : cc);
  };

  /**
   * D401: a market's lifecycle status no longer changes on the select's `onChange`. It fired straight from the
   * control, so one mis-click retired a live market with no confirmation and no stated reason. The operator now
   * confirms and picks a reason from the closed vocabulary.
   *
   * D420 (owner decision D408 #9): the reason is persisted. The dialog's chips show translated LABELS, so the label the
   * operator picked is mapped back to its stable CODE before it travels; the optional note travels with it, and the
   * server writes both on the market's audit row in the same transaction as the status. D449: ACTIVE is a proposal another operator
   * approves (four eyes; the D483 carry).
   */
  const onChangeStatus = async (id: string, status: MarketLaunchStatus): Promise<void> => {
    setStatusMsg(undefined);
    const market = markets.find((m) => m.id === id);
    const labels = MARKET_STATUS_REASONS.map((code) => v('market.status_reason', code));
    const answer = await confirm({
      title: t('w6.marketStatus.title', { name: market?.name ?? id, status: v('market.launch_status', status) }),
      body: t('w6.marketStatus.body'),
      confirmLabel: t('w6.marketStatus.confirm'),
      cancelLabel: t('ui.cancel'),
      tone: status === 'RETIRED' || status === 'PAUSED' || status === 'EXITING' ? 'danger' : 'primary',
      icon: 'badgeCheck',
      reasons: labels,
      reasonLabel: t('w6.reason.label'),
      notePlaceholder: t('mktgov.note.placeholder'),
    });
    if (!answer.confirmed || answer.reason === undefined) return;
    const reasonCode = MARKET_STATUS_REASONS[labels.indexOf(answer.reason)] as MarketStatusReasonCode | undefined;
    if (reasonCode === undefined) return;
    setStatusBusy(id);
    if (status === 'ACTIVE') {
      // D449: ACTIVE is never one person's act — the change becomes a proposal another operator approves below.
      const proposed = await runtime.markets.proposeActivation(id, { reasonCode, ...(answer.note !== undefined ? { note: answer.note } : {}) });
      setStatusBusy(undefined);
      setStatusMsg(proposed.ok
        ? { tone: 'success', text: t('mact.proposed', { id }) }
        : { tone: 'danger', text: proposed.error.reasonCode === 'MARKET_ACTIVATION_PENDING' ? t('mact.pending') : t('mkt.status.failed', { code: fail(proposed.error) }) });
      await loadActivations();
      return;
    }
    const res = await setMarketStatus(runtime, id, status, { reasonCode, ...(answer.note !== undefined ? { note: answer.note } : {}) });
    setStatusBusy(undefined);
    if (res.ok) {
      setStatusMsg({ tone: 'success', text: t('mkt.status.changed', { id, status: v('market.launch_status', status) }) });
      setMarkets((ms) => ms.map((m) => (m.id === id ? res.value.market : m)));
      if (history?.market.id === id) void openHistory(res.value.market);
    } else {
      setStatusMsg({ tone: 'danger', text: res.error.reasonCode === 'MARKET_STATUS_UNCHANGED' ? t('mktgov.unchanged') : t('mkt.status.failed', { code: fail(res.error) }) });
    }
  };

  const openHistory = async (market: OperatingMarketView): Promise<void> => {
    setHistory({ market, changes: undefined });
    const res = await marketStatusHistory(runtime, market.id);
    setHistory(res.ok ? { market, changes: res.value.changes } : { market, changes: [], error: t('mktgov.history.failed', { code: res.error.code }) });
  };

  const reasonLabel = (code: string): string => v('market.status_reason', code);

  const columns: readonly Column<OperatingMarketView>[] = [
    { key: 'id', header: t('mkt.col.id'), render: (m) => <span className="tnum">{m.id}</span> },
    { key: 'name', header: t('mkt.col.name'), render: (m) => m.name },
    { key: 'region', header: t('mkt.col.region'), render: (m) => (m.regionCode === undefined ? '—' : place.region(m.regionCode)) },
    { key: 'timezone', header: t('mkt.col.timezone'), render: (m) => m.timezone },
    { key: 'status', header: t('mkt.col.status'), render: (m) => <Badge tone={STATUS_TONE[m.status] ?? 'neutral'}>{v('market.launch_status', m.status)}</Badge> },
    {
      key: 'action',
      header: t('mkt.col.action'),
      render: (m) => (
        <Select
          aria-label={t('mkt.status.change')}
          value={m.status}
          disabled={statusBusy !== undefined}
          onChange={(e) => void onChangeStatus(m.id, e.target.value as MarketLaunchStatus)}
          className="vello-ab-minw"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {v('market.launch_status', s)}
            </option>
          ))}
        </Select>
      ),
    },
    {
      key: 'history',
      header: t('mktgov.history'),
      render: (m) => (
        <Button variant="ghost" aria-label={t('mktgov.history.of', { name: m.name })} onClick={() => void openHistory(m)}>
          {t('mktgov.history')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('mkt.title')} subtitle={t('mkt.subtitle')} />

      <Panel>
        <Stack gap={4}>
          <SectionTitle>{t('mkt.register')}</SectionTitle>
          <Text as="p" tone="secondary">{t('mkt.register.hint')}</Text>
          <form onSubmit={(e) => void onCreate(e)} noValidate>
            <Stack gap={4}>
              <FormGrid>
                <Field label={t('mkt.field.country')} htmlFor="mkt-country" hint={errors.countryCode ?? t('mkt.field.country.hint')}>
                  <TextInput id="mkt-country" value={form.countryCode} onChange={(e) => set('countryCode')(e.target.value.toUpperCase())} maxLength={2} placeholder="US" required />
                </Field>
                <Field label={t('mkt.field.slug')} htmlFor="mkt-slug" hint={errors.slug ?? t('mkt.field.slug.hint')}>
                  <TextInput id="mkt-slug" value={form.slug} onChange={(e) => set('slug')(e.target.value.toLowerCase())} placeholder="chicago" required />
                </Field>
                <Field label={t('mkt.field.name')} htmlFor="mkt-name" hint={errors.name ?? t('mkt.field.name.hint')}>
                  <TextInput id="mkt-name" value={form.name} onChange={(e) => set('name')(e.target.value)} placeholder="Chicago" required />
                </Field>
                <Field label={t('mkt.field.timezone')} htmlFor="mkt-tz" hint={errors.timezone ?? t('mkt.field.timezone.hint')}>
                  <TextInput id="mkt-tz" value={form.timezone} onChange={(e) => set('timezone')(e.target.value)} placeholder="America/Chicago" required />
                </Field>
                <Field label={t('mkt.field.region')} htmlFor="mkt-region" hint={errors.regionCode ?? t(regionChoices.length > 0 ? 'mkt.field.region.hint' : regionCountry === '' || regionCountry.length < 2 ? 'mkt.field.region.pickCountry' : 'mkt.field.region.empty')}>
                  <Select id="mkt-region" value={form.regionCode} disabled={regionChoices.length === 0} onChange={(e) => set('regionCode')(e.target.value)}>
                    <option value="">{t('mkt.field.region.none')}</option>
                    {regionChoices.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                  </Select>
                </Field>
                <Field label={t('mkt.field.lat')} htmlFor="mkt-lat" hint={errors.latitude ?? t('mkt.field.lat.hint')}>
                  <TextInput id="mkt-lat" value={form.latitude} onChange={(e) => set('latitude')(e.target.value)} inputMode="decimal" placeholder="41.8781" />
                </Field>
                <Field label={t('mkt.field.lng')} htmlFor="mkt-lng" hint={errors.longitude ?? t('mkt.field.lng.hint')}>
                  <TextInput id="mkt-lng" value={form.longitude} onChange={(e) => set('longitude')(e.target.value)} inputMode="decimal" placeholder="-87.6298" />
                </Field>
              </FormGrid>
              {createMsg !== undefined && (
                <Notice tone={createMsg.tone}>{createMsg.text}</Notice>
              )}
              <Button type="submit" variant="primary" disabled={createBusy}>
                {createBusy ? t('mkt.create.busy') : t('mkt.create')}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Panel>

      <Panel>
        <Stack gap={4}>
          <SectionTitle>{t('mkt.registry')}</SectionTitle>
          <FormRow columns="minmax(0, 220px)"
            action={
              <Button disabled={listBusy} onClick={() => void onLookup()}>
                {listBusy ? t('mkt.lookup.busy') : t('mkt.lookup')}
              </Button>
            }
          >
            <Field label={t('mkt.field.lookup')} htmlFor="mkt-lookup" hint={t('mkt.field.lookup.hint')}>
              <TextInput id="mkt-lookup" value={lookup} onChange={(e) => setLookup(e.target.value.toUpperCase())} maxLength={2} placeholder="US" />
            </Field>
          </FormRow>
          {listMsg !== undefined && <Notice tone="danger">{listMsg}</Notice>}
          {statusMsg !== undefined && (
            <Notice tone={statusMsg.tone}>{statusMsg.text}</Notice>
          )}
          {activations.length > 0 && (
            <Stack gap={2} data-testid="mkt-activations">
              <strong>{t('mact.title')}</strong>
              {activations.map((r) => (
                <Cluster gap={2} justify="between" wrap key={r.id}>
                  <span>
                    {t('mact.row', { id: r.marketId, from: v('market.launch_status', r.fromStatus), to: v('market.launch_status', 'ACTIVE') })}
                    {r.mine === true && !selfApproval && <Text tone="secondary" block>{t('opr.queue.mine')}</Text>}
                  </span>
                  <Cluster gap={2}>
                    {(r.mine !== true || selfApproval) && <Button size="sm" variant="secondary" onClick={() => void decideActivation(r, 'approve')} data-testid={`mact-approve-${r.id}`}>{t('opr.queue.approve')}</Button>}
                    {r.mine !== true && <Button size="sm" variant="ghost" onClick={() => void decideActivation(r, 'reject')}>{t('opr.queue.reject')}</Button>}
                    {r.mine === true && <Button size="sm" variant="ghost" onClick={() => void decideActivation(r, 'withdraw')}>{t('opr.queue.withdraw')}</Button>}
                  </Cluster>
                </Cluster>
              ))}
            </Stack>
          )}
          {loaded && (
            <Table columns={columns} rows={markets} rowKey={(m) => m.id} empty={<Notice tone="neutral">{t('mkt.empty')}</Notice>} />
          )}
        </Stack>
      </Panel>

      <Panel>
        <Stack gap={4}>
          <SectionTitle>{t('rgn.title')}</SectionTitle>
          <RegionsConsole />
        </Stack>
      </Panel>

      {history !== undefined && (
        <Panel>
          <Stack gap={4}>
            <SectionTitle>{t('mktgov.history.title', { name: history.market.name })}</SectionTitle>
            {history.error !== undefined && <Notice tone="danger">{history.error}</Notice>}
            {history.changes === undefined ? (
              <LoadingBlock lines={2} />
            ) : history.changes.length === 0 ? (
              <Text as="p" tone="secondary">{t('mktgov.history.empty')}</Text>
            ) : (
              <Stack as="ol" gap={3}>
                {history.changes.map((c) => (
                  <TimelineItem key={c.changeId}>
                    <strong>{t('mktgov.history.transition', { from: v('market.launch_status', c.fromStatus), to: v('market.launch_status', c.toStatus) })}</strong>
                    <span>{reasonLabel(c.reasonCode)}{c.note !== undefined ? ` — ${c.note}` : ''}</span>
                    <Text role="caption" tone="secondary" mono>
                      {formatMarketInstant(c.at, history.market.timezone, lang)} · {t('mktgov.who', { operator: actorName(c.actorId) ?? tailOf(c.actorId) })}
                    </Text>
                  </TimelineItem>
                ))}
              </Stack>
            )}
          </Stack>
        </Panel>
      )}
    </>
  );
}
